import useHasServiceChoice from '@/hooks/useHasServiceChoice';
import useResolveService from '@/hooksApi/useResolveService';
import { useFetchServicePreferences } from '@/hooksApi/useServicePreference';
import type { OrderBoxInput, ServicePickup, ServiceResolution } from '@/types';

/**
 * SVC-STEP increment 2 — does this order get the Select Service step, or is it
 * already decided?
 *
 * ONE SOURCE, read by create-order navigation, edit-order navigation and the
 * summary's trace. Letting each caller decide for itself is precisely how the
 * stepper came to render a step the flow skipped (QA SVC-F2), and the cost
 * here would be worse: a skip nobody agreed on ships a service the merchant
 * never saw.
 *
 * ⚠️ NEVER SKIP ON AN UNKNOWN ANSWER. While the resolution is loading, or if
 * it failed, `shouldShowStep` is TRUE. Skipping costs the merchant a service
 * they did not choose; showing costs them one click. The asymmetry is the
 * whole argument, and it is why the default is not `false`.
 */
export interface ServiceStepDecision {
  /** Show the step. True whenever the answer is unknown. */
  shouldShowStep: boolean;
  /** The wire's answer, or undefined while unknown. */
  resolution: ServiceResolution | undefined;
  isResolving: boolean;
}

/**
 * The rungs that mean SOMETHING THE MERCHANT IS BOUND TO already decided, so
 * asking again every order is friction rather than choice.
 *
 * `platform_default` is deliberately NOT here: it means nobody has decided
 * anything, which is exactly the merchant who chose "I'll choose for each
 * order" in Settings. `none` is not here either — nothing resolved at all, so
 * there is no decision to honour and the merchant must land in the step.
 *
 * ⚠️ `plan_assignment` WAS here and was removed on 2026-08-22 (Alhassan Ahmed's
 * CR, approved by the product owner). A committed tier plan still OUTRANKS the
 * other rungs when resolving which service applies — that is unchanged — but
 * outranking is not the same as deciding on the merchant's behalf, and a tier
 * merchant was reaching Summary having never been shown what they ship on.
 * `BOXY_SELECT_GUIDE.md` §4–5 is the design of record and was updated with it.
 *
 * Scope was deliberately ONLY this entry. The no-choice guards below
 * (`hasServiceChoice`, `is_only_option`) stay: a one-row page with a CONTINUE
 * button is friction, not transparency. BOXY-590 narrowed `is_only_option`
 * to merchants with a saved service — see below.
 */
const DECIDED_SOURCES = ['province_rule', 'account_default'];

const useServiceStepDecision = (
  provinceCode?: string | null,
  enabled = true,
  temperatureControl?: boolean,
  regionUid?: string | null,
  pickup?: ServicePickup,
  boxes?: OrderBoxInput[],
): ServiceStepDecision => {
  const hasServiceChoice = useHasServiceChoice();
  const { data: preferences } = useFetchServicePreferences();
  const { data: resolution, isLoading } = useResolveService(
    provinceCode,
    enabled && hasServiceChoice,
    temperatureControl,
    regionUid,
    pickup,
    boxes,
  );

  // Nothing to choose between — the catalogue's own state, and the same count
  // the stepper rail reads.
  if (!hasServiceChoice) {
    return { shouldShowStep: false, resolution, isResolving: false };
  }

  if (!resolution) {
    return { shouldShowStep: true, resolution: undefined, isResolving: isLoading };
  }

  // BOXY-590 (AC2, same rule as the merchant app): a "saved service" is the
  // merchant's OWN default or a province rule for this destination — never the
  // plan's default. With nothing saved the merchant lands in the step and
  // picks, even where `is_only_option` (e.g. only Select takes these boxes):
  // the step greys the rest out with the reason. Saved merchants: unchanged.
  const hasSavedService =
    Boolean(preferences?.default_service_code) ||
    (preferences?.provinces ?? []).some((rule) => rule.province_code === provinceCode);
  if (!hasSavedService) {
    return { shouldShowStep: true, resolution, isResolving: false };
  }

  if (resolution.is_only_option) {
    return { shouldShowStep: false, resolution, isResolving: false };
  }

  // Owner ruling 2026-08-26, via the PM: a Baghdad→Boxy Save rule and a COLD
  // Baghdad order must SHOW this step and say the rule does not fit, instead of
  // skipping to Summary and refusing there.
  //
  // ⭐ ONE SENTENCE, NOT TWO RULES: the merchant's own setting skips the step
  // UNLESS it cannot be used for this order. `unavailable_preference` is
  // already the backend's channel for "your setting could not be used" — it
  // carries `retired`, `not_offered` and now `not_capable` — so every reason
  // routes here identically and a new one needs no client change.
  //
  // It also closes the case the 2026-08-25 ruling left open on this screen: a
  // merchant whose PROVINCE RULE failed but whose ACCOUNT DEFAULT resolved got
  // a decided source, skipped the step, and was told only on Summary.
  const settingUnusable = resolution.unavailable_preference != null;

  return {
    shouldShowStep: settingUnusable || !DECIDED_SOURCES.includes(resolution.source),
    resolution,
    isResolving: false,
  };
};

export default useServiceStepDecision;
